import { Injectable, inject } from '@angular/core';
import { LocalStorageService } from './local-storage.service';
import { AuthResponse } from '../../../Interfaces/api/auth/auth-response.interface';

@Injectable({
  providedIn: 'root',
})
export class AuthTokenService {
  private readonly _localStorage = inject(LocalStorageService);

  private readonly TOKEN_KEY = 'auth_token';
  private readonly REFRESH_TOKEN_KEY = 'auth_refresh_token';
  private readonly TOKEN_EXPIRY_KEY = 'auth_token_expiry';
  private readonly RETURN_URL_KEY = 'auth_return_url';

  setToken(auth_data: AuthResponse): void {
    this._localStorage.setItem(this.TOKEN_KEY, auth_data.access_token);
    this._localStorage.setItem(
      this.TOKEN_EXPIRY_KEY,
      this._expiresAtFrom(auth_data.expires_in).toString(),
    );

    if (auth_data.refresh_token) {
      this._localStorage.setItem(this.REFRESH_TOKEN_KEY, auth_data.refresh_token);
    } else {
      this._localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    }
  }

  getToken(): string | null {
    return this._localStorage.getItem(this.TOKEN_KEY);
  }

  getRefreshToken(): string | null {
    return this._localStorage.getItem(this.REFRESH_TOKEN_KEY);
  }

  hasRefreshToken(): boolean {
    return !!this.getRefreshToken();
  }

  getExpiresAt(): number | null {
    const stored = this._localStorage.getItem(this.TOKEN_EXPIRY_KEY);

    if (stored === null) {
      return null;
    }

    const parsed = parseInt(stored, 10);

    return Number.isNaN(parsed) ? null : parsed;
  }

  isTokenExpired(): boolean {
    const expires_at = this.getExpiresAt();

    if (expires_at === null) {
      return true;
    }

    return this._now() > expires_at;
  }

  hasValidToken(): boolean {
    return !!this.getToken() && !this.isTokenExpired();
  }

  getJwtPayload(): Record<string, unknown> | null {
    const token = this.getToken();

    if (!token) {
      return null;
    }

    const segments = token.split('.');

    if (segments.length !== 3) {
      return null;
    }

    try {
      return JSON.parse(atob(segments[1].replace(/-/g, '+').replace(/_/g, '/')));
    } catch {
      return null;
    }
  }

  isImpersonated(): boolean {
    return this.getJwtPayload()?.['impersonated'] === true;
  }

  clearToken(): void {
    this._localStorage.removeItem(this.TOKEN_KEY);
    this._localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    this._localStorage.removeItem(this.TOKEN_EXPIRY_KEY);
  }

  setReturnUrl(return_url: string): void {
    this._localStorage.setItem(this.RETURN_URL_KEY, return_url);
  }

  getAndClearReturnUrl(): string {
    const stored = this._localStorage.getItem(this.RETURN_URL_KEY) ?? '/';
    this._localStorage.removeItem(this.RETURN_URL_KEY);

    return stored.startsWith('/') && !stored.startsWith('//') ? stored : '/';
  }

  private _expiresAtFrom(expires_in: number): number {
    return this._now() + expires_in * 1000;
  }

  private _now(): number {
    return Date.now();
  }
}
